import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton, Chip, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import SydSwitch from '../../../../../../../commonDesign/SydSwitch'
import { formatDisplayName } from '../../../../../../../../utils/formatters'
import { blueprintExportFormats } from '../../../../../../../organisms/QueryBlueprint/admin/blueprintExportFormats'
import ColumnConfigEditor from './ColumnConfigEditor'
import LatchModePicker from './LatchModePicker'
import StyleVariantPicker from './StyleVariantPicker'

function ParamMappingEditor ({ childPurposeCode, parentPurposeCode, allPurposes, paramMapping, onChange }) {
  const requiredParams = allPurposes[childPurposeCode]?.masterPredicate?.requiredParams ?? []
  const columnOptions = useMemo(() => {
    const parentColumns = allPurposes[parentPurposeCode]?.columns ?? []
    return [
      { label: '(unmapped)', value: '' },
      ...parentColumns.map(col => ({
        label: formatDisplayName(col.name),
        value: col.name
      }))
    ]
  }, [allPurposes, parentPurposeCode])

  const handleChange = useCallback((paramName, columnName) => {
    const next = { ...(paramMapping || {}) }
    if (columnName) {
      next[paramName] = columnName
    } else {
      delete next[paramName]
    }
    onChange(next)
  }, [paramMapping, onChange])

  if (!childPurposeCode) {
    return (
      <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.75rem' }}>
        (select a purpose)
      </span>
    )
  }

  if (requiredParams.length === 0) {
    return (
      <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.75rem' }}>
        (no parameters)
      </span>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {requiredParams.map(param => (
        <div key={param} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.75rem', minWidth: '80px', fontFamily: 'monospace' }}>{param}</span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-color-subtle)' }}>&larr;</span>
          <SydSelect
            options={columnOptions}
            value={paramMapping?.[param] || ''}
            onChange={(e) => handleChange(param, e.target.value)}
            size='sm'
          />
        </div>
      ))}
    </div>
  )
}

ParamMappingEditor.propTypes = {
  childPurposeCode: PropTypes.string,
  parentPurposeCode: PropTypes.string,
  allPurposes: PropTypes.object.isRequired,
  paramMapping: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

function HierarchicalGridConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)
  const [newVariationGroup, setNewVariationGroup] = useState('')
  const [showVariationInput, setShowVariationInput] = useState(false)

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const levels = localConfig.hierarchy?.levels || []

  // Gather columns from the first level's purpose for the column config
  const primaryPurposeCode = levels[0]?.purposeCode
  const primaryPurposeColumns = useMemo(() => {
    if (!primaryPurposeCode || !allPurposes[primaryPurposeCode]) return []
    return allPurposes[primaryPurposeCode].columns || []
  }, [primaryPurposeCode, allPurposes])

  const columnSelectOptions = useMemo(() => {
    return primaryPurposeColumns.map(col => ({
      label: formatDisplayName(col.name),
      value: col.name
    }))
  }, [primaryPurposeColumns])

  const availableColumnsWithLabel = useMemo(() => {
    const labelCol = localConfig.hierarchy?.labelColumn || '_hierarchyLabel'
    const syntheticCol = { name: labelCol, dataType: 'string', description: 'Synthetic hierarchy label' }
    return [syntheticCol, ...primaryPurposeColumns]
  }, [primaryPurposeColumns, localConfig.hierarchy?.labelColumn])

  // --- Level management ---
  const handleAddLevel = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: [...(prev.hierarchy?.levels || []), { purposeCode: '', paramMapping: {} }]
      }
    }))
  }, [])

  const handleRemoveLevel = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: (prev.hierarchy?.levels || []).filter((_, i) => i !== index)
      }
    }))
  }, [])

  const handleUpdateLevelPurpose = useCallback((index, purposeCode) => {
    setLocalConfig(prev => {
      const prevLevels = prev.hierarchy?.levels || []
      const updatedLevels = prevLevels.map((lvl, i) =>
        i === index ? { ...lvl, purposeCode } : lvl
      )

      // Auto-populate paramMapping for child levels by matching required
      // param names to parent columns with the same name
      if (index > 0 && purposeCode) {
        const parentCode = updatedLevels[index - 1]?.purposeCode
        const parentColNames = new Set(
          (allPurposes[parentCode]?.columns || []).map(c => c.name)
        )
        const childParams = allPurposes[purposeCode]?.masterPredicate?.requiredParams || []
        const autoMapping = {}
        for (const param of childParams) {
          if (parentColNames.has(param)) {
            autoMapping[param] = param
          }
        }
        updatedLevels[index] = { ...updatedLevels[index], paramMapping: autoMapping }
      }

      return {
        ...prev,
        hierarchy: { ...prev.hierarchy, levels: updatedLevels }
      }
    })

    // When the first level purpose changes, auto-fill columns
    if (index === 0) {
      const purpose = allPurposes[purposeCode]
      if (purpose && purpose.columns) {
        const prefillColumns = purpose.columns.map(col => ({
          name: col.name,
          displayName: formatDisplayName(col.name),
          description: col.description || null,
          formatString: null,
          align: ['integer', 'decimal'].includes(col.dataType) ? 'right' : 'left',
          sortable: col.sortable !== false,
          filterable: col.filterable !== false,
          hidden: col.hidden || false,
          exclude: false,
          width: 150
        }))
        setLocalConfig(prev => {
          const labelCol = prev.hierarchy?.labelColumn || '_hierarchyLabel'
          const labelEntry = {
            name: labelCol,
            displayName: 'Name',
            description: null,
            formatString: null,
            align: 'left',
            sortable: true,
            filterable: false,
            hidden: false,
            exclude: false,
            width: 300
          }
          return { ...prev, columns: [labelEntry, ...prefillColumns] }
        })
      }
    }
  }, [allPurposes])

  const handleSetParamMapping = useCallback((levelIndex, mapping) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: (prev.hierarchy?.levels || []).map((lvl, i) =>
          i === levelIndex ? { ...lvl, paramMapping: mapping } : lvl
        )
      }
    }))
  }, [])

  const handleInitialFetchDepthChange = useCallback((value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: { ...prev.hierarchy, initialFetchDepth: Number(value) || 1 }
    }))
  }, [])

  const handleExpandDepthChange = useCallback((value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: { ...prev.hierarchy, defaultExpandDepth: Number(value) || 0 }
    }))
  }, [])

  // --- Column management ---
  const handleAddColumn = useCallback((column) => {
    setLocalConfig(prev => ({ ...prev, columns: [...prev.columns, column] }))
  }, [])

  const handleRemoveColumn = useCallback((index) => {
    setLocalConfig(prev => ({ ...prev, columns: prev.columns.filter((_, i) => i !== index) }))
  }, [])

  const handleUpdateColumn = useCallback((index, updated) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => i === index ? updated : col)
    }))
  }, [])

  const handleColumnsChange = useCallback((newColumns) => {
    setLocalConfig(prev => ({ ...prev, columns: newColumns }))
  }, [])

  // --- Default sort ---
  const handleAddSortField = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: [...(prev.defaultSort || []), { field: '', dir: 'asc' }]
    }))
  }, [])

  const handleRemoveSortField = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.filter((_, i) => i !== index)
    }))
  }, [])

  const handleUpdateSortField = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.map((sort, i) =>
        i === index ? { ...sort, [field]: value } : sort
      )
    }))
  }, [])

  const sortFieldOptions = useMemo(() => {
    return (localConfig.columns || []).map(col => ({
      label: col.displayName || col.name,
      value: col.name
    }))
  }, [localConfig.columns])

  const sortDirOptions = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' }
  ]

  // --- Variation groups ---
  const handleAddVariationGroup = useCallback(() => {
    const trimmed = newVariationGroup.trim().toLowerCase().replace(/\s+/g, '-')
    if (!trimmed) return
    if ((localConfig.variationGroups || []).includes(trimmed)) {
      setNewVariationGroup('')
      setShowVariationInput(false)
      return
    }
    setLocalConfig(prev => ({
      ...prev,
      variationGroups: [...(prev.variationGroups || []), trimmed]
    }))
    setNewVariationGroup('')
    setShowVariationInput(false)
  }, [newVariationGroup, localConfig.variationGroups])

  const handleRemoveVariationGroup = useCallback((groupToRemove) => {
    setLocalConfig(prev => ({
      ...prev,
      variationGroups: (prev.variationGroups || []).filter(g => g !== groupToRemove)
    }))
  }, [])

  return (
    <Grid container spacing={2}>
      {/* Hierarchy Levels */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Hierarchy Levels</h4>
          <SydButton size='sm' icon='add' onClick={handleAddLevel}>
            Add Level
          </SydButton>
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)', marginTop: 0, marginBottom: '12px' }}>
          Each level is a separate query purpose. Child levels receive parent row values via parameter mapping.
        </p>
        {levels.length > 0
          ? (
            <Table className='opal-presentation-table zebra-rows' size='small' style={{ marginBottom: '12px' }}>
              <TableHead className='opt-thead'>
                <TableRow className='opt-tr'>
                  <TableCell className='opt-th' style={{ width: '40px' }}>#</TableCell>
                  <TableCell className='opt-th'>Purpose</TableCell>
                  <TableCell className='opt-th'>Parameter Mapping</TableCell>
                  <TableCell className='opt-th' style={{ width: '48px' }} />
                </TableRow>
              </TableHead>
              <TableBody className='opt-tbody'>
                {levels.map((level, index) => (
                  <TableRow className='opt-tr' key={index}>
                    <TableCell className='opt-td'>{index + 1}</TableCell>
                    <TableCell className='opt-td'>
                      <SydSelect
                        options={purposeOptions}
                        value={level.purposeCode || ''}
                        onChange={(e) => handleUpdateLevelPurpose(index, e.target.value)}
                        size='sm'
                      />
                    </TableCell>
                    <TableCell className='opt-td'>
                      {index === 0
                        ? (
                          <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.75rem' }}>
                            (root level, no mapping)
                          </span>
                        )
                        : (
                          <ParamMappingEditor
                            childPurposeCode={level.purposeCode}
                            parentPurposeCode={levels[index - 1]?.purposeCode}
                            allPurposes={allPurposes}
                            paramMapping={level.paramMapping}
                            onChange={(mapping) => handleSetParamMapping(index, mapping)}
                          />
                        )}
                    </TableCell>
                    <TableCell className='opt-td'>
                      <IconButton size='small' onClick={() => handleRemoveLevel(index)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
          : (
            <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', margin: '0 0 12px' }}>
              No levels configured. Add at least one level.
            </p>
          )}
        <div style={{ display: 'flex', gap: '16px' }}>
          <SydLabel label='Initial Fetch Depth' helperText='How many levels to pre-fetch on load'>
            <SydInput
              type='number'
              value={localConfig.hierarchy?.initialFetchDepth ?? 1}
              onChange={(e) => handleInitialFetchDepthChange(e.target.value)}
              style={{ maxWidth: '120px' }}
            />
          </SydLabel>
          <SydLabel label='Default Expand Depth' helperText='How many levels to auto-expand (0 = collapsed)'>
            <SydInput
              type='number'
              value={localConfig.hierarchy?.defaultExpandDepth ?? 0}
              onChange={(e) => handleExpandDepthChange(e.target.value)}
              style={{ maxWidth: '120px' }}
            />
          </SydLabel>
        </div>
        <div style={{ marginTop: '12px' }}>
          <SydLabel label='Label Column Name' helperText='Synthetic field name written on every row with the display label. Reference this in your column config.'>
            <SydInput
              value={localConfig.hierarchy?.labelColumn ?? '_hierarchyLabel'}
              onChange={(e) => {
                const newLabel = e.target.value || '_hierarchyLabel'
                setLocalConfig(prev => {
                  const prevLabel = prev.hierarchy?.labelColumn || '_hierarchyLabel'
                  if (prevLabel === newLabel) {
                    return { ...prev, hierarchy: { ...prev.hierarchy, labelColumn: newLabel } }
                  }
                  return {
                    ...prev,
                    hierarchy: { ...prev.hierarchy, labelColumn: newLabel },
                    columns: (prev.columns || []).map(col =>
                      col.name === prevLabel ? { ...col, name: newLabel } : col
                    ),
                    defaultSort: (prev.defaultSort || []).map(s =>
                      s.field === prevLabel ? { ...s, field: newLabel } : s
                    )
                  }
                })
              }}
              placeholder='_hierarchyLabel'
              style={{ maxWidth: '240px' }}
            />
          </SydLabel>
        </div>
        <div style={{ marginTop: '12px' }}>
          <SydLabel label='Leaf Label Accessor' helperText='Source column for leaf row labels. Leave empty if leaves do not need a label.'>
            <SydSelect
              options={[{ label: '(None)', value: '' }, ...columnSelectOptions]}
              value={localConfig.hierarchy?.leafLabelAccessor || ''}
              onChange={(e) => setLocalConfig(prev => ({
                ...prev,
                hierarchy: { ...prev.hierarchy, leafLabelAccessor: e.target.value || undefined }
              }))}
            />
          </SydLabel>
        </div>
      </Grid>

      <LatchModePicker
        purpose={allPurposes[primaryPurposeCode]}
        value={localConfig.latchMode}
        onChange={(latchMode) => setLocalConfig(prev => ({ ...prev, latchMode }))}
      />

      {/* Hierarchical Filter Toggle */}
      <Grid item xs={12}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SydSwitch
            value={localConfig.enableHierarchicalFilter !== false}
            onChange={(e, checked) => setLocalConfig(prev => ({ ...prev, enableHierarchicalFilter: checked }))}
          />
          <span style={{ fontSize: '0.875rem' }}>Enable Hierarchical Filter</span>
        </div>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-color-subtle)', marginTop: '4px' }}>
          When enabled, shows a search box that filters across the tree while preserving parent/child context.
        </p>
      </Grid>

      {/* Column Configuration */}
      <Grid item xs={12}>
        <ColumnConfigEditor
          columns={localConfig.columns || []}
          availableColumns={availableColumnsWithLabel}
          onAddColumn={handleAddColumn}
          onRemoveColumn={handleRemoveColumn}
          onUpdateColumn={handleUpdateColumn}
          onColumnsChange={handleColumnsChange}
        />
      </Grid>

      {/* Default Sort */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Default Sort Order</h4>
          <SydButton
            size='sm'
            icon='add'
            onClick={handleAddSortField}
            disabled={(localConfig.columns || []).length === 0}
          >
            Add Sort
          </SydButton>
        </div>
        {(localConfig.defaultSort || []).map((sort, index) => (
          <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ flex: 1 }}>
              <SydSelect
                options={sortFieldOptions}
                value={sort.field}
                onChange={(e) => handleUpdateSortField(index, 'field', e.target.value)}
                placeholder='Select field'
              />
            </div>
            <div>
              <SydSelect
                options={sortDirOptions}
                value={sort.dir}
                onChange={(e) => handleUpdateSortField(index, 'dir', e.target.value)}
              />
            </div>
            <IconButton size='small' onClick={() => handleRemoveSortField(index)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </div>
        ))}
        {(localConfig.defaultSort || []).length === 0
          ? (
            <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', margin: 0 }}>
              No default sort configured
            </p>
          )
          : null}
      </Grid>

      {/* Variation Groups */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Variation Groups</h4>
          {!showVariationInput
            ? (
              <SydButton size='sm' icon='add' onClick={() => setShowVariationInput(true)}>
                Add Group
              </SydButton>
            )
            : null}
        </div>
        {showVariationInput
          ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
              <SydInput
                value={newVariationGroup}
                onChange={(e) => setNewVariationGroup(e.target.value)}
                placeholder='Enter group name'
                style={{ maxWidth: '200px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); handleAddVariationGroup() }
                  if (e.key === 'Escape') { setShowVariationInput(false); setNewVariationGroup('') }
                }}
                autoFocus
              />
              <SydButton size='sm' variant='primary' onClick={handleAddVariationGroup} disabled={!newVariationGroup.trim()}>
                Add
              </SydButton>
              <SydButton size='sm' variant='ghost' onClick={() => { setShowVariationInput(false); setNewVariationGroup('') }}>
                Cancel
              </SydButton>
            </div>
          )
          : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {(localConfig.variationGroups || []).map((group) => (
            <Chip
              key={group}
              label={group}
              onDelete={() => handleRemoveVariationGroup(group)}
              variant='outlined'
              size='small'
            />
          ))}
          {(localConfig.variationGroups || []).length === 0
            ? (
              <span style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
                No variation groups configured
              </span>
            )
            : null}
        </div>
      </Grid>

      {/* Local Storage Key */}
      <Grid item xs={12}>
        <SydLabel label='Local Storage Key' helperText='Unique key for storing user column preferences'>
          <SydInput
            value={localConfig.localStorageKey || ''}
            onChange={(e) => setLocalConfig(prev => ({ ...prev, localStorageKey: e.target.value }))}
          />
        </SydLabel>
      </Grid>

      {/* Export Settings */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Export Settings</h4>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <SydSwitch
            value={!!localConfig.export?.enabled}
            onChange={(e, enabled) => setLocalConfig(prev => ({
              ...prev,
              export: enabled
                ? { ...prev.export, enabled: true, formats: prev.export?.formats || ['csv'] }
                : { ...prev.export, enabled: false }
            }))}
          />
          <span style={{ fontSize: '0.875rem' }}>Enable Export</span>
        </div>
        {localConfig.export?.enabled
          ? (
            <>
              <Table className='opal-presentation-table zebra-rows' size='small' style={{ marginBottom: '16px' }}>
                <TableBody className='opt-tbody'>
                  {blueprintExportFormats.map(fmt => (
                    <TableRow className='opt-tr' key={fmt.key}>
                      <TableCell className='opt-td' style={{ width: '48px' }}>
                        <SydSwitch
                          value={(localConfig.export?.formats || []).includes(fmt.key)}
                          onChange={(e, checked) => {
                            setLocalConfig(prev => {
                              const current = prev.export?.formats || []
                              const formats = checked
                                ? [...current, fmt.key]
                                : current.filter(f => f !== fmt.key)
                              if (!formats.length) {
                                return { ...prev, export: { ...prev.export, enabled: false, formats } }
                              }
                              return { ...prev, export: { ...prev.export, formats } }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell className='opt-td'>{fmt.label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <SydSwitch
                  value={localConfig.export?.flattenOnExport !== false}
                  disabled
                />
                <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
                  Flatten tree on export (coming soon)
                </span>
              </div>
              <SydLabel label='Default File Name' helperText='Pre-filled name when users export (optional)'>
                <SydInput
                  value={localConfig.export?.defaultFileName || ''}
                  onChange={(e) => setLocalConfig(prev => ({
                    ...prev,
                    export: { ...prev.export, defaultFileName: e.target.value || undefined }
                  }))}
                  placeholder='blueprint-export'
                />
              </SydLabel>
            </>
          )
          : null}
      </Grid>

      <StyleVariantPicker
        presentationType='Hierarchical Grid'
        value={localConfig.styleVariant}
        onChange={(v) => setLocalConfig(prev => ({ ...prev, styleVariant: v, styleVariantParams: {} }))}
        variantParams={localConfig.styleVariantParams}
        onParamsChange={(p) => setLocalConfig(prev => ({ ...prev, styleVariantParams: p }))}
      />
    </Grid>
  )
}

HierarchicalGridConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default HierarchicalGridConfigEditor
